import { action, makeObservable, observable, runInAction } from "mobx";
import type { LoadedModel, Model } from "../models";
import apiService from "./apiService";
import type { PromiseCompletion } from "../classes/PromiseCompletion";
import { helperService } from "./helperService";

export type PullProgress = {
  status: string;
  digest?: string;
  total?: number;
  completed?: number;
  error?: string;
  message?: string;
};

export type PullModelOptions = {
  onProgress?: (progress: PullProgress) => void;
  signal?: AbortSignal;
};

class ModelService {
  @observable activeModels: LoadedModel[] = [];
  @observable models: Model[] = [];
  /** All models including embedding/reranking (for settings/admin views). */
  @observable allModels: Model[] = [];
  @observable prefetchingModel: string | null = null;
  private _abortController = new AbortController();

  constructor() {
    makeObservable(this);
  }

  public loadActiveModels = async () => {
    const activeModels = await apiService.get<LoadedModel[]>('/tags/active');
    runInAction(() => {
      this.activeModels = activeModels;
    });
  }
  
  public loadModels = async (loader?: PromiseCompletion) => {
    const tags = await apiService.get<Model[]>('/tags/full', { loader: loader });

    // remove all deepseek-ocr from the list
    const isAdmin = window.user?.role === 'admin';
    // filter embedding and reranking models, and any -ocr models, unless user is admin
    const filteredTags = tags.filter(tag => {
      if (tag.capabilities.includes('embedding') || tag.capabilities.includes('embeddings') || tag.capabilities.includes('reranking')
      || tag.name.toLowerCase().includes('embed') || tag.name.toLowerCase().includes('rerank')) {
        return false;
      }
      if (!isAdmin && tag.name.includes('ocr')) {
        return false;
      }
      return true;
    });

    runInAction(() => {
      // allModels: unfiltered (includes embedding/reranking for admin), used by settings tab
      this.allModels = isAdmin ? tags : filteredTags;
      this.models = filteredTags;
      //sort by server, artificial_analysis_intelligence_index and getModelName(), higher index first

      const sortFn = (a: Model, b: Model) => {
        // sort by IsFavorite descending
        const aFavorite = a.isFavorite ? 1 : 0;
        const bFavorite = b.isFavorite ? 1 : 0;
        if (aFavorite !== bFavorite) return bFavorite - aFavorite;
        const serverCompare = a.server.localeCompare(b.server);
        if (serverCompare !== 0) return serverCompare;
        const aIndex = a.artificial_analysis_intelligence_index ?? Number.MAX_SAFE_INTEGER;
        const bIndex = b.artificial_analysis_intelligence_index ?? Number.MAX_SAFE_INTEGER;
        if (aIndex !== bIndex) return bIndex - aIndex;
        return this.getModelName(a).localeCompare(this.getModelName(b));
      };

      this.models.sort(sortFn);
      // allModels may differ from models (includes embedding/reranking for admin)
      if (this.allModels !== this.models) {
        this.allModels.sort(sortFn);
      }
    });
  };
  
  public getQuantizationTag(model: Model): string {
    const q = model.quantization_level;
    if (!q) return '';
    // Normalize to uppercase display form
    return q.toUpperCase();
  }

  public getModelName(model: Model | string): string {
    if (typeof model === 'string') {
      const modelLoaded = this.models.find(m => m.name === model);
      if (!modelLoaded)
        return model;

      model = modelLoaded;
    }

    // If a custom display name has been set, use it directly
    if (model.displayName) {
      return model.displayName;
    }

    let modelName = model?.name || '';
    //if modelName ends with :latest, replace with actual parameters size

    let parameter_size = model?.parameter_size ?
      model.parameter_size.replace(/^(\d+(?:\.\d+)?)(B)$/, (_, p1, p2) => 
        Math.round(parseFloat(p1)-0.01) + p2
      )
      : '';

    // if modelName contains parameter size like -30B, -7b etc, use it as parameter_size and remove from modelName
    const paramSizeMatch = modelName.match(/-(\d+(?:\.\d+)?[bB])/);
    if (paramSizeMatch) {
      const sizeStr = paramSizeMatch[1];
      modelName = modelName.replace(`-${sizeStr}`, '');
      parameter_size = sizeStr.toUpperCase();
    }

    //remove quantization level from model name
    // Handles: q4_0, q8_0, q4_k_m, q4_k_s, q4_k_l, q4_k_xl, q4_k_xs, q6_k, q3_k_u
    //          iq4_xs, iq3_m, iq2_xxs, iq1_s  (IQ-series)
    //          fp16, bf16, f16, f32 handled further down by BF16 removal
    modelName = modelName.replace(/[:-]?(?:iq\d+_[a-z0-9]+|q\d+(?:_(?:\d+|[a-z]+(?:_[a-z0-9]+)*))?)/gi, '');

    // remove active parameters size like a3b or a11B 
    modelName = modelName.replace(/-?[aA]\d+[bB]/, '');

    if (model && modelName.endsWith(':latest')) {
      modelName = modelName.replace(':latest', `:${parameter_size}`);
    }
    if (model && modelName.endsWith(model.quantization_level)) {
      modelName = modelName.replace(model.quantization_level, `${parameter_size}`);
    }

    //remove -GGUF or GGUF from model name (case insensitive), MXFP4 / MOE tags (e.g. llama3.3-gguf-moe) should br converted to llama3.3
    modelName = modelName.replace(/[-_]?GGUF/i, ''); // Remove -GGUF or GGUF if present
    modelName = modelName.replace(/[-_]?MXFP4/i, ''); // Remove -MXFP4 or MXFP4 if present
    modelName = modelName.replace(/[-_]?MOE/i, ''); // Remove -MOE or MOE if present
    modelName = modelName.replace(/[-_]?instruct/i, ''); // Remove -instruct or instruct if present
    modelName = modelName.replace(/[-_]?tainted/i, ''); // Remove -tainted or tainted if present
    modelName = modelName.replace(/[-_]?BF16/i, ''); // Remove -BF16 or BF16 if present
    modelName = modelName.replace(/kldzj_/i, ''); // Remove kldzj_ if present
    
    if (modelName && modelName.includes('/')) {
      modelName = modelName.split('/').pop() || modelName; // Get the last part after '/'
    }

    // final step: trim any trailing hyphens or colons, model-_.:20b => model:20b
    const modelParts = modelName.split(':');
    let [ baseName, size ] = modelParts;
    baseName = baseName.replace(/[-_\.]+$/g, ''); // Remove trailing hyphens/underscores
    modelName = size ? `${baseName}:${size.toUpperCase()}` : `${baseName}:${parameter_size}`;

    return modelName;
  }

  @action
  public async prefetchModel(model: Model, tokensInConversation: number) {
    const activeModels = await apiService.get<LoadedModel[]>('/tags/active');

    const activeModel = activeModels.find(m => m.name === model.name && m.server === model.server);
    if (activeModel) return;

    this._abortController.abort();
    this._abortController = new AbortController();
    this.prefetchingModel = model.name;
    try{
      await apiService.post('/tags/prefetch/' + encodeURIComponent(model.name) + '?tokensInConversation=' + tokensInConversation, null, {
        headers: {
          'Content-Type': 'application/json',
          'x-ollama-server': model.server
        },
        signal: this._abortController.signal,
      });
    } finally {
      runInAction(() => {
        this.prefetchingModel = null;
      });
    }
    
    await this.loadActiveModels();
  }

  /**
   * Pull a model from Ollama registry
   * @param modelName The name of the model to pull (e.g., "llama3.3", "qwen2.5:14b")
   * @param options Optional configuration with progress callback and abort signal
   * @returns Promise that resolves to true on success, false on error
   */
  @action
  public async pullModel(modelName: string, options: PullModelOptions = {}): Promise<boolean> {
    const { onProgress, signal } = options;

    const wakeLock = await helperService.getWakeLock();

    try {
      onProgress?.({ status: 'Starting pull...' });

      const response = await fetch('/api/model/pull', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modelName: modelName.trim() }),
        signal: signal
      });

      if (!response.ok) {
        throw new Error(`Pull failed: ${response.statusText}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n').filter(line => line.trim());

        for (const line of lines) {
          try {
            const progress: PullProgress = JSON.parse(line);

            onProgress?.(progress);

            if (progress.error) {
              throw new Error(progress.error);
            }

            if (progress.status === 'success') {
              await this.loadModels();
              onProgress?.({ status: 'Pull completed!', message: progress.message });
              return true;
            }
          } catch (e: any) {
            if (e.message && !e.message.includes('JSON')) {
              throw e;
            }
          }
        }
      }

      return true;
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        onProgress?.({ status: 'Error', error: err.message });
      }
      return false;
    } finally {
      wakeLock?.release();
    }
  }

  /**
   * Check if a model has an update available
   * @param modelName The name of the model to check
   * @returns Object with hasUpdate flag and optional newVersion
   */
  public async checkModelUpdate(modelName: string): Promise<{ hasUpdate: boolean; currentDigest?: string; latestDigest?: string; message?: string }> {
    try {
      const response = await apiService.get<{ hasUpdate: boolean; currentDigest?: string; latestDigest?: string; message?: string }>(
        `/api/model/${encodeURIComponent(modelName)}/update-status`
      );
      return response;
    } catch (error) {
      console.error('Failed to check model update:', error);
      return { hasUpdate: false };
    }
  }

  /**
   * Set a custom display name for a model. Pass null or empty string to restore the original name.
   */
  public async renameModel(modelName: string, displayName: string | null): Promise<void> {
    await apiService.put(`/api/model/${encodeURIComponent(modelName)}/rename`, { displayName: displayName || null });
    // Update the local model cache immediately
    runInAction(() => {
      const model = this.models.find(m => m.name === modelName);
      if (model) {
        model.displayName = displayName || null;
      }
    });
  }
}

export default new ModelService();